import { Components, Panel, Vertex2 } from '@rta/rta-blueprint-library';

import { applyVectorMask } from './apply-vector-mask';
import { getPanelItemVectors } from '@r-core/helpers';
import { mergeOverlappingMaskLoops } from './merge-overlapping-mask-loops';
import { Layer, type Vector } from '@r-core/types';

type ApplyPanelItemMasksParams = {
	panel: Panel;
	xOffset: number,
	yOffset: number
	allVectors: Array<Vector<Vertex2>>;
	isCountertop?: boolean;
	counterThickness?: number;
	applyMask?: boolean;
};

/**
 * Renders panel items (appliances) for both top and front views
 */
export const applyPanelItemMasks = (params: ApplyPanelItemMasksParams): Array<Vector<Vertex2>> => {
	const { panel, xOffset, yOffset, allVectors, isCountertop = false, counterThickness = 0, applyMask = true } = params;
	let vectors = allVectors;
	const panelItemGroups = panel?.panelItemGroups;
	const maskVectorsToApply: Array<Vector<Vertex2>> = [];
	if (!panelItemGroups?.children) return vectors;

	// console.log('applyPanelItemMasks:start', { applyMask, isCountertop, allVectors });

	panelItemGroups.children.forEach((panelItemGroup) => {
		panelItemGroup.items.filterItems(null).forEach((panelItem) => {
			const skuItem = panelItem.item;
			const yCenter = isCountertop
				? -panelItem.center.z + (panel.countertopOverhang / 2)
				: panelItem.isFullHeightAppliance
					? 0
					: panelItem.center.y;

			const oCenter = new Vertex2({ dataIn: {
				x: panelItemGroup.center.x + panelItem.center.x,
				y: yCenter
			}});
			// console.log('yCenter', { yCenter, panelItemGroup: panelItemGroup.center.z, panelItemCenter: panelItem.center.z });

			// Skip standalone items, no cutout masks for them
			if (panelItem.isStandAlone) return; // Skip this item

			// Skip trim kits - they sit on top of appliances and don't cut into the panel
			if (skuItem.sku?.endsWith('TK')) return;

			// skip anything that doesn't mask the countertop when isCountertop is true
			if (isCountertop && !(panelItem.item.placement === Components.PLACEMENT_TOP_ONLY || panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP)) return;

			// skip anything that doesn't mask the panel when isCountertop is false
			if (!isCountertop && panelItem.item.placement === Components.PLACEMENT_TOP_ONLY) return;

			// panelItemGroup.center.y is relative to panel center, and panelYOffset is the panel center
			// panelItemGroup.center.y is inverted (blueprint coordinates), so we negate it
			const applianceVectors = getPanelItemVectors({ panelItem, isCountertop }).filter((vector) => vector.layer === Layer.Panel);


			if (panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP) {
				oCenter.y = isCountertop
					? panelItem.center.z
					// ? (panel.size.depth / -2) + (panel.countertopOverhang * 2)
					: ((panel.size.height - panelItem.item.distanceBelowCountertop + counterThickness) / -2);
			} else if (panelItem.item.placement === Components.PLACEMENT_TOP_ONLY) {
				oCenter.y = panelItem.center.z;
			}

			// apply the offsets after positoining
			oCenter.x += xOffset;
			oCenter.y += yOffset;

			// Create new vector objects to avoid mutating shared references
			const transformedVectors = applianceVectors.map((vector) => {
				const newFrom = new Vertex2({
					dataIn: {
						x: oCenter.x + vector.from.x,
						y: oCenter.y + vector.from.y
					}
				});
				const newTo = new Vertex2({
					dataIn: {
						x: oCenter.x + vector.to.x,
						y: oCenter.y + vector.to.y
					}
				});
				return {
					...vector,
					from: newFrom,
					to: newTo
				};
			});

			const panelAngle = panel.getOriginAngle();
			if (isCountertop && panelAngle !== 0) {
				transformedVectors.forEach((vector) => {
					vector.from.rotateAxis(new Vertex2({ dataIn: { x: xOffset, y: yOffset }}), panelAngle);
					vector.to.rotateAxis(new Vertex2({ dataIn: { x: xOffset, y: yOffset }}), panelAngle);
				});
			}

			maskVectorsToApply.push(...transformedVectors);
		});
	});

	const maskVectors = mergeOverlappingMaskLoops(maskVectorsToApply);

	if (applyMask) {
		vectors = applyVectorMask({ shapeVectors: vectors, maskVectors });
	} else {
		vectors = [
			...vectors,
			...(maskVectors.map((vector) => ({
				...vector,
				from: new Vertex2({ dataIn: { x: vector.from.x, y: vector.from.y }}),
				to: new Vertex2({ dataIn: { x: vector.to.x, y: vector.to.y }})
			})) as Array<Vector<Vertex2>>)
		];
	}

	return vectors;
};
